import React, { FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { Dropdown } from "@multiweb/dropdown";
import { ButtonBackgroundColor, IconButton } from "@multiweb/reactComponents";
import { bemClassNames, EMPTY_ARRAY } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { FrameSharedProps } from "../../types";
import { RowAction } from "../RowAction";
import { RowActionDropdownItem } from "../RowActionDropdownItem";
import { WorkflowActionItems } from "../WorkflowActionItems";
import { WorkflowButton } from "../WorkflowButton";

export type RowActionsComponentProps = FrameSharedProps & {
    /** Identifikátory akcí, které se májí zobrazit. */
    actionIds?: ReadonlyArray<string>;
    /** Určuje, zda jsou akce zakázány. */
    disabled?: boolean;
    /** Příznak jestli třída podporuje workflow */
    hasWorkflow?: boolean;
    /** Šířka rodiče, která je k dispozici */
    maxWidth?: number;
    /** Funkce, jejíž volání zajistí načtení akcí */
    loadWorkflowActions: () => void;
    /** Příznak, zda jsou workflow akce načítány */
    isWorkflowLoading: boolean;
};

const classNames = bemClassNames("row-actions-component");
const DROPDOWN_WIDTH = 200;

export const RowActionsComponent: FunctionComponent<RowActionsComponentProps> = ({
    dataSourceKey,
    recordId,
    actionIds = EMPTY_ARRAY as ReadonlyArray<string>,
    disabled,
    hasWorkflow,
    maxWidth = 0,
    loadWorkflowActions,
    isWorkflowLoading,
}) => {
    const [ visibleCount, setVisibleCount ] = useState(actionIds.length + (hasWorkflow ? 1 : 0));
    const [sizeMap, setSizeMap] = useState(new Map());
    const addToMap = (id: string, width: number) => {
        setSizeMap(new Map(sizeMap.set(id, width)));
    };
    const [isWorkflowHidden, setIsWorkflowHidden] = useState(false);
    const [ dropdownTriggerWidth, setDropdownTriggerWidth ] = useState<number>(0);
    const dropdownRef = useCallback((node) => {
        if (node && node.clientWidth > 0)
            setDropdownTriggerWidth(node.clientWidth);
    }, []);

    useEffect(() => {
        let sum = 0;
        const values = Array.from(sizeMap.values());

        for (let i = 0; i < values.length; i++) {
            sum += values[i];
            if (sum >= ((maxWidth || 0) - dropdownTriggerWidth)) {
                setVisibleCount(i - 1);
                return;
            }
        }
        setVisibleCount(values.length);
    }, [sizeMap, maxWidth, actionIds, dropdownTriggerWidth]);

    const calculatedGroups = useMemo(() => {
        const visibleGroup: ReactElement[] = [];
        const hiddenGroup: ReactElement[] = [];

        actionIds.forEach((actionId, index) => {
            if (index < visibleCount) {
                visibleGroup.push(<RowAction
                    key={actionId}
                    {...{ dataSourceKey, recordId, actionId, disabled }}
                    onSizeCallback={addToMap} />);
            } else {
                hiddenGroup.push(<RowActionDropdownItem key={actionId}
                    {...{ dataSourceKey, recordId, actionId, disabled }}/>);
            }
        });

        if (hasWorkflow) {
            if (visibleGroup.length < visibleCount) {
                visibleGroup.push(
                    <WorkflowButton
                        key={recordId}
                        {...{ dataSourceKey, recordId, disabled }}
                        onSizeCallback={addToMap}
                    />,
                );
                setIsWorkflowHidden(false);
            } else {
                hiddenGroup.push(
                    <WorkflowActionItems
                        key={recordId}
                        {...{ dataSourceKey, recordId, disabled }}
                    />,
                );
                setIsWorkflowHidden(true);
            }
        }

        return { visibleGroup, hiddenGroup };

    // Je žádoucí odchytávat jen při změně visible, kvůli předešlému hooku, který reaguje na ostatní
    // a mění právě visible
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleCount, isWorkflowLoading]);

    const handleDropdownVisibilityChange = useCallback((isOpen: boolean) => {
        if (isOpen && isWorkflowHidden) {
            loadWorkflowActions();
        }
    }, [loadWorkflowActions, isWorkflowHidden]);

    return (
        <div className={classNames()}>
            { calculatedGroups.visibleGroup }
            { calculatedGroups.hiddenGroup.length > 0 &&
                <div ref={dropdownRef}>
                    <Dropdown
                        width={DROPDOWN_WIDTH}
                        maxWidth={maxWidth}
                        onVisibilityChange={handleDropdownVisibilityChange}
                        triggerRenderer={(triggerButtonProps) => (
                            <IconButton
                                iconCode={IconCode.musicNextA }
                                backgroundColor={ButtonBackgroundColor.transparent}
                                {...triggerButtonProps}
                                {...{ disabled }}
                            />
                        )}>
                        { calculatedGroups.hiddenGroup }
                    </Dropdown>
                </div>
            }
        </div>
    );
};
